// Свободная область поля — окно за вычетом краев, занятых интерфейсом матча.
// Элементы HUD, которые закрывают поле, помечены атрибутом
// data-field-inset="top|bottom|left|right" (сторона окна, которую они
// занимают); useFieldInsets меряет их и кладет результат сюда, а
// GameScene ставит по нему кадры камеры (centerOnFreeArea).
//
// Не Redux: значения меняются на каждом кадре анимации футера и нужны только
// сцене, а перерисовывать из-за них React незачем

export const FIELD_INSET_ATTRIBUTE = 'data-field-inset';
// Элемент считается, только если окно не уже этой ширины — для панелей,
// которые закрывают поле лишь в десктопной раскладке, а на телефоне
// выдвигаются поверх и кадр менять не должны
export const FIELD_INSET_MIN_WIDTH_ATTRIBUTE = 'data-field-inset-min-width';

export const FIELD_INSET_SIDES = Object.freeze(['top', 'right', 'bottom', 'left']);

export const ZERO_FIELD_INSETS = Object.freeze({top: 0, right: 0, bottom: 0, left: 0});

/**
 * Отступы свободной области из прямоугольников элементов интерфейса.
 * Сторона берется по самому глубокому элементу: верх — по нижнему краю самого
 * низкого из верхних, низ — от верхнего края самого высокого из нижних и т.д.
 * Элементы вне окна (задвинутая панель) и пустые (скрытые) не в счет
 *
 * @param {Array<{side: string, rect: {left: number, top: number, right: number, bottom: number, width: number, height: number}}>} entries
 * @param {{width: number, height: number}} viewport — CSS-пиксели
 * @returns {{top: number, right: number, bottom: number, left: number}} CSS-пиксели, целые
 */
export const calcFieldInsets = (entries, viewport) => {
  const insets = {...ZERO_FIELD_INSETS};
  (entries || []).forEach(({side, rect}) => {
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) {
      return;
    }
    const isOnScreen = rect.right > 0 && rect.left < viewport.width
      && rect.bottom > 0 && rect.top < viewport.height;
    if (!isOnScreen) {
      return;
    }
    if (side === 'top') {
      insets.top = Math.max(insets.top, rect.bottom);
    } else if (side === 'bottom') {
      insets.bottom = Math.max(insets.bottom, viewport.height - rect.top);
    } else if (side === 'left') {
      insets.left = Math.max(insets.left, rect.right);
    } else if (side === 'right') {
      insets.right = Math.max(insets.right, viewport.width - rect.left);
    }
  });
  // Половина окна — предел: перекрытая насквозь сторона (панель на весь экран)
  // не должна выворачивать свободную область наизнанку
  const maxHorizontal = viewport.width / 2;
  const maxVertical = viewport.height / 2;
  return {
    top: Math.round(Math.min(Math.max(0, insets.top), maxVertical)),
    right: Math.round(Math.min(Math.max(0, insets.right), maxHorizontal)),
    bottom: Math.round(Math.min(Math.max(0, insets.bottom), maxVertical)),
    left: Math.round(Math.min(Math.max(0, insets.left), maxHorizontal)),
  };
};

export const checkIfFieldInsetsEqual = (a, b) => FIELD_INSET_SIDES.every((side) => a?.[side] === b?.[side]);

let currentInsets = ZERO_FIELD_INSETS;
const listeners = new Set();

/** Последние отступы свободной области (CSS-пиксели) */
export const getFieldInsets = () => currentInsets;

/**
 * Опубликовать новые отступы. Одинаковые подписчикам не рассылаются
 * @param {{top: number, right: number, bottom: number, left: number}} insets
 */
export const setFieldInsets = (insets) => {
  if (checkIfFieldInsetsEqual(insets, currentInsets)) {
    return;
  }
  currentInsets = Object.freeze({...insets});
  listeners.forEach((listener) => listener(currentInsets));
};

/**
 * @param {function({top: number, right: number, bottom: number, left: number}): void} listener
 * @returns {function(): void} отписка
 */
export const subscribeFieldInsets = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const FIELD_INSETS_MEASURE_EVENT = 'fieldinsets:measure';

/**
 * Попросить useFieldInsets перемерить элементы. Нужно, когда элемент доехал сдвигом: размер
 * не менялся, класс тоже, и наблюдатели конечного положения не увидят
 */
export const requestFieldInsetsMeasure = () => {
  document.dispatchEvent(new Event(FIELD_INSETS_MEASURE_EVENT));
};

/** Сброс при уходе с экрана матча (и в тестах) */
export const resetFieldInsets = () => setFieldInsets(ZERO_FIELD_INSETS);
